<template>
	<div>
		<div v-if="!substudyActive" class="alert alert-danger text-center">
			<strong>Inactive</strong>
		</div>
		<div class="card">
			<div class="card-header">
				<h3>
					<a href="/substudies">Substudies:</a> {{ substudy.name }}
				</h3>
			</div>
			<div class="card-body">
				<div v-if="saving">
					<div class="d-flex justify-content-center align-items-center bg-dark opacity-50 w-100 h-100 position-absolute top-0 start-0">
					  	<div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
					  	<span class="ms-2">
					  		<h3 class="text-primary">Saving...</h3>
					  	</span>
				  	</div>
				</div>

				<div>
					<div class="d-flex justify-content-between mb-4">
						<div class="w-25">
							<input class="form-control" type="text" v-model="filter" placeholder="Search participants by STAR ID or name" @input="fetchDebounced">
						</div>
						<div v-if="substudyActive">
							<span class="fst-italic">Bulk task assignment affects only eligible and enrolled participants</span>
							<button class="btn btn-primary ms-2" :disabled="selectedParticipants.length === 0" @click="bulkAssignTasks">Assign Substudy Task</button>
							<button class="btn btn-primary ms-2" :disabled="selectedParticipants.length === 0" @click="openEditInfoForm">Edit Info</button>
						</div>
						<div v-else>
							<span class="fst-italic text-danger">Task assignment and participant updates are disabled for inactive substudies.</span>
						</div>
					</div>

					<table class="table">
						<thead>
							<tr>
								<th>
									<span class="sort d-flex justify-contents-center align-items-baseline" @click="applySort('star_id')">
	                                    <span>STAR ID</span>
	                                    <down-arrow v-if="sortDirection == 'asc' || sortField != 'star_id'" /> 
	                                    <up-arrow v-if="sortDirection == 'desc' && sortField == 'star_id'" />
	                                </span>
								</th>
								<th>
									<span class="sort d-flex justify-contents-center align-items-baseline" @click="applySort('first_name')">
	                                    <span>Name</span>
	                                    <down-arrow v-if="sortDirection == 'asc' || sortField != 'first_name'" /> 
	                                    <up-arrow v-if="sortDirection == 'desc' && sortField == 'first_name'" />
	                                </span>
	                            </th>
								<th>
									<span class="sort d-flex justify-contents-center align-items-baseline" @click="applySort('age')">
	                                    <span>Age</span>
	                                    <down-arrow v-if="sortDirection == 'asc' || sortField != 'age'" /> 
	                                    <up-arrow v-if="sortDirection == 'desc' && sortField == 'age'" />
	                                </span>
								</th>
								<th>
									<span class="sort d-flex justify-contents-center align-items-baseline" @click="applySort('hiv_status_id')">
	                                    <span>HIV Status</span>
	                                    <down-arrow v-if="sortDirection == 'asc' || sortField != 'hiv_status_id'" /> 
	                                    <up-arrow v-if="sortDirection == 'desc' && sortField == 'hiv_status_id'" />
	                                </span>
								</th>
								<th>
									<span class="sort d-flex justify-contents-center align-items-baseline" @click="applySort('site_id')">
	                                    <span>Site</span>
	                                    <down-arrow v-if="sortDirection == 'asc' || sortField != 'site_id'" /> 
	                                    <up-arrow v-if="sortDirection == 'desc' && sortField == 'site_id'" />
	                                </span>
								</th>
								<th>
									<span class="sort d-flex justify-contents-center align-items-baseline" @click="applySort('is_spanish_speaking')">
	                                    <span>Language</span>
	                                    <down-arrow v-if="sortDirection == 'asc' || sortField != 'is_spanish_speaking'" /> 
	                                    <up-arrow v-if="sortDirection == 'desc' && sortField == 'is_spanish_speaking'" />
	                                </span>
								</th>
								<th>
									<span class="sort d-flex justify-contents-center align-items-baseline" @click="applySort('eligibility')">
	                                    <span>Eligibility</span>
	                                    <down-arrow v-if="sortDirection == 'asc' || sortField != 'eligibility'" /> 
	                                    <up-arrow v-if="sortDirection == 'desc' && sortField == 'eligibility'" />
	                                </span>
								</th>
								<th>
									<span class="sort d-flex justify-contents-center align-items-baseline" @click="applySort('enrollment')">
	                                    <span>Enrollment</span>
	                                    <down-arrow v-if="sortDirection == 'asc' || sortField != 'enrollment'" /> 
	                                    <up-arrow v-if="sortDirection == 'desc' && sortField == 'enrollment'" />
	                                </span>
								</th>
								<th>
									<span class="sort d-flex justify-contents-center align-items-baseline" @click="applySort('last_exam_date')">
	                                    <span>Last Exam</span>
	                                    <down-arrow v-if="sortDirection == 'asc' || sortField != 'last_exam_date'" /> 
	                                    <up-arrow v-if="sortDirection == 'desc' && sortField == 'last_exam_date'" />
	                                </span>
								</th>
								<th v-if="substudyActive">Upcoming Task</th>
								<th v-if="substudyActive">
									<input type="checkbox" 
										id="select-all-checkbox"
										v-model="allChecked"
										@input="toggleAllSelectedParticipants"
									>
								</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="participant in participants" :key="participant.id">
								<td>{{ participant.star_id }}</td>
								<td><a :href="'/participants/'+participant.id">{{ participant.full_name }}</a></td>
								<td>{{ participant.age }}</td>
								<td>{{ participant.hiv_status }}</td>
								<td>{{ participant.site }}</td>
								<td>{{ participant.language }}</td>
								<td>{{ participant.eligibility }}</td>
								<td>{{ participant.enrollment }}</td>
								<td>{{ participant.last_exam_date }}</td>
								<td v-if="substudyActive && !isEligible(participant)">&nbsp;</td>
								<td v-if="substudyActive && isEligible(participant) && participant.current_task !== null">
									<a :href="'/tasks/'+participant.current_task.id">{{ substudy.name }}</a>
								</td>
								<td v-if="substudyActive && isEligible(participant) && participant.current_task == null">
									<button class="btn btn-sm btn-outline-primary" 
										@click="assignSingleTask(participant.id)" 
										:disabled="selectedParticipants.length !== 0">
										Assign Substudy Task
									</button>
								</td>
								<td v-if="substudyActive">
									<input type="checkbox" 
										:id="'checkbox_'+participant.id" 
										:value="participant.id"
										v-model="selectedParticipants"
									>
								</td>
							</tr>
							
						</tbody>
					</table>
				</div>
			</div>
			<substudy-edit-info-form></substudy-edit-info-form>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import _ from 'lodash'
import DownArrow from '../shared/DownArrow.vue'
import UpArrow from '../shared/UpArrow.vue'
import { Modal } from 'bootstrap'
import SubstudyEditInfoForm from './SubstudyEditInfoForm.vue'

export default {
	name: "Substudy Detail",
	components: {
		DownArrow,
		UpArrow,
		SubstudyEditInfoForm
	},
	props: {
		substudy: Object,
		eligibilityStatusOptions: Object,
		enrollmentStatusOptions: Object,
	},
	data() {
		return {
			allChecked: false,
			filter: null,
			modal: null,
			substudyActive: this.substudy.active
		}
	},
	methods: {
		applySort(field) {
			if(field == this.sortField) {
                let direction = this.sortDirection == 'asc' ? 'desc' : 'asc'
                this.$store.dispatch('substudies/setSortDirection', direction)
            }
            else {
                this.$store.dispatch('substudies/setSortDirection', 'asc')
                this.$store.dispatch('substudies/setSortField', field)
            }

            if (!['eligibility', 'enrollment', 'last_exam_date'].includes(this.sortField)) {
            	this.$store.dispatch('substudies/fetchParticipants')
            }
		},
		assignSingleTask(part_id) {
			this.$store.commit('substudies/updateSelectedParticipants', [part_id])
			this.bulkAssignTasks()
		},
		bulkAssignTasks() {
			this.$store.dispatch('substudies/assignTask')
		},
		fetchDebounced: _.debounce(function() {
			this.$store.dispatch('substudies/setFilter', this.filter)
            this.$store.dispatch('substudies/fetchParticipants')
        }, 500),
        isEligible(participant) {
        	return participant.eligibility == 'Eligible' && participant.enrollment == 'Enrolled'
        },
        openEditInfoForm() {
        	this.$store.commit('substudies/setEditInfoFormOpen', true)
        },
        toggleAllSelectedParticipants() {
        	this.$store.dispatch('substudies/toggleAllSelectedParticipants', this.allChecked)
        }
	},
	computed: {
		...mapState({
			editInfoFormOpen: state => state.substudies.editInfoFormOpen,
            saving: state => state.substudies.saving,
            stateParticipants: state => state.substudies.participants,
            sortDirection: state => state.substudies.sortDirection,
            sortField: state => state.substudies.sortField
        }),
        participants () {
        	if (['eligibility', 'enrollment', 'last_exam_date'].includes(this.sortField)) {
        		return this.stateParticipants.sort((a, b) => {
        			// equal items sort equally
				    if (a[this.sortField] === b[this.sortField]) {
				        return 0;
				    }

				    // nulls sort after anything else
				    if (a[this.sortField] === null) {
				        return 1;
				    }
				    if (b[this.sortField] === null) {
				        return -1;
				    }

				    // otherwise, if we're ascending, lowest sorts first
				    if (this.sortDirection == 'asc') {
				        return a[this.sortField] < b[this.sortField] ? -1 : 1;
				    }

				    // if descending, highest sorts first
				    return a[this.sortField] < b[this.sortField] ? 1 : -1;
        		})
        	}
        	return this.stateParticipants
        },
        selectedParticipants: {
		    get () {
		      	return this.$store.state.substudies.selectedParticipants
		    },
		    set (value) {
		      	this.$store.commit('substudies/updateSelectedParticipants', value)
		    }
		}
	},
	watch: {
        editInfoFormOpen (newVal, oldVal) {
            newVal ? this.modal.show() : this.modal.hide()
        }
    },
	mounted() {
		this.modal = new Modal( document.getElementById('edit-info-form-modal') )

		this.$store.dispatch('substudies/setSubstudy', this.substudy)
		this.$store.dispatch('substudies/setEligibilityStatusOptions', this.eligibilityStatusOptions)
		this.$store.dispatch('substudies/setEnrollmentStatusOptions', this.enrollmentStatusOptions)
		this.$store.dispatch('substudies/fetchParticipants')
	}
}
</script>

<style scoped>
	.sort {
		cursor: pointer;
	}
</style>